// /managers/ConvoManager/streamParser.jsx
/* ========================
 * parseUserMessageContent - Parses user's raw message for UI display
 * Hides transcription blocks by removing them from the content before rendering.
 * ========================*/
import { StreamEventTypes } from './UniversalResponseSchema';
import { generateId } from './ConvoHelpers';

export const parseUserMessageContent = (rawContent) => {
  if (typeof rawContent !== 'string') return [];

  // Define the markers for content to be hidden.
  const systemTag = '[SYSTEM_AWAITING_TRANSCRIPT]';
  const transcribedRegex = /\\[Transcribed\\]([\s\S]*?)\\[\/Transcribed\\]/g;

  // Start with the original content.
  let visibleContent = rawContent;

  // Remove the marked blocks from the string.
  visibleContent = visibleContent.replace(transcribedRegex, '');
  visibleContent = visibleContent.replace(systemTag, '');
  
  // Trim any whitespace left over from the removal.
  visibleContent = visibleContent.trim();

  // If there's any visible content remaining, create a text block for it.
  if (visibleContent) {
    return [{
      type: 'text',
      id: `text_${generateId()}`,
      content: visibleContent,
    }];
  }

  // If nothing is left (e.g., a voice-only message), return an empty array.
  return [];
};


/* ========================
 * parseAssistantMessageStream - Parses a full array of assistant events at once
 * ========================*/
export const parseAssistantMessageStream = (streamEvents) => {
  // ARCHITECTURAL FIX: Corrected check from Array() constructor to Array.isArray().
  // This was the root cause of historical messages rendering as blank.
  if (!Array.isArray(streamEvents)) return [];
  
  let blocks = [];
  let toolCallsMap = {};

  for (const event of streamEvents) {
    blocks = updateContentBlocksIncremental(blocks, event, toolCallsMap);
  }
  
  return finalizeContentBlocks(blocks);
};


/* ========================
 * updateContentBlocksIncremental - Updates content blocks from a single stream event
 * This is the new, correct implementation that matches the server's event schema.
 * ========================*/
export const updateContentBlocksIncremental = (existingBlocks, event, toolCallsMap) => {
  let newBlocks = [...existingBlocks];
  const { payload } = event;
  // Make event type mutable for normalization
  let { type } = event;

  if (!type || !payload) return newBlocks;

  // ARCHITECTURAL FIX: Normalize event type to UPPER_SNAKE_CASE before processing.
  // This makes the client resilient to inconsistencies from the server (which was
  // sending lowercase tool events) and fixes the root cause of the bug.
  if (typeof type === 'string') {
    type = type.toUpperCase();
  }

  // The switch statement now reliably handles events regardless of original casing.
  switch (type) {
    case StreamEventTypes.BLOCK_CREATE: {
      newBlocks.push({ ...payload, content: payload.content || '', isFinal: false });
      break;
    }
    
    case StreamEventTypes.BLOCK_DELTA: {
      const { id, contentChunk, replace } = payload;
      let blockExists = false;
      newBlocks = newBlocks.map(block => {
        if (block.id === id) {
          blockExists = true;
          const newContent = replace ? contentChunk : (block.content || '') + contentChunk;
          return { ...block, content: newContent };
        }
        return block;
      });
      if (!blockExists) {
         console.warn(`[StreamParser] Received BLOCK_DELTA for non-existent block ID: ${id}. Discarding chunk.`);
      }
      break;
    }
    
    case StreamEventTypes.BLOCK_END: {
      const { id } = payload;
      newBlocks = newBlocks.map(block => block.id === id ? { ...block, isFinal: true } : block);
      break;
    }

    case StreamEventTypes.TOOL_CALL_START: {
      const { toolCallId, toolName } = payload;
      const blockId = `tool_block_${toolCallId}`;
      const existingBlock = newBlocks.find(block => block.id === blockId && block.type === 'tool_code');

      if (existingBlock) {
        toolCallsMap[toolCallId] = { blockId: existingBlock.id };
        break;
      }

      if (!toolCallsMap[toolCallId]) {
        const newBlock = {
          id: blockId,
          type: 'tool_code',
          toolName: toolName,
          toolArgs: '',
          result: null,
          status: 'pending',
          isFinal: false,
        };
        newBlocks.push(newBlock);
        toolCallsMap[toolCallId] = { blockId: newBlock.id };
      }
      break;
    }

    case StreamEventTypes.TOOL_CALL_END: {
      const { toolCallId, input } = payload;
      const toolInfo = toolCallsMap[toolCallId];
      if (toolInfo) {
        newBlocks = newBlocks.map(block => 
          block.id === toolInfo.blockId ? { ...block, toolArgs: input } : block
        );
      }
      break;
    }
    
    case StreamEventTypes.TOOL_PROCESSING_START: {
      const { toolCallId } = payload;
      const toolInfo = toolCallsMap[toolCallId];
      if (toolInfo) {
        newBlocks = newBlocks.map(block =>
          block.id === toolInfo.blockId ? { ...block, status: 'processing' } : block
        );
      }
      break;
    }
    
    case StreamEventTypes.TOOL_PROCESSING_RESULT: {
      const { toolCallId, result, isError } = payload;
      const toolInfo = toolCallsMap[toolCallId];

      let cleanedResult = result;
      // ARCHITECTURAL FIX: Remove internal tool metadata from the result string.
      // This ensures only the relevant data is displayed to the user.
      if (typeof result === 'string') {
        const match = result.match(/^\]+\] \s*(.*)$/);
        if (match && match[1]) {
          cleanedResult = match[1];
        }
      }

      if (toolInfo) {
        newBlocks = newBlocks.map(block => {
          if (block.id === toolInfo.blockId) {
            return {
              ...block,
              result: cleanedResult, 
              status: isError ? 'error' : 'success',
              isFinal: true,
            };
          }
          return block;
        });
      } else {
        console.warn(`[StreamParser] TOOL_PROCESSING_RESULT received for unknown toolCallId: ${toolCallId}. Attempting to find block directly.`);
        const expectedBlockId = `tool_block_${toolCallId}`;
        newBlocks = newBlocks.map(block => {
          if (block.id === expectedBlockId && block.type === 'tool_code') {
            return {
              ...block,
              result: cleanedResult, 
              status: isError ? 'error' : 'success',
              isFinal: true,
            };
          }
          return block;
        });
      }
      break;
    }
  }
  return newBlocks;
};

// Defines tools that are "dispatch-style" where the backend stream ending
// while the tool is "processing" signifies a successful handoff, not an interruption.
const DISPATCH_TOOLS = [
    'My_Workspaces',
    'My_Projects',
    'Shelbula_Coder', 
    'Admin', 
    'Task_Swarm'
].map(t => t.toLowerCase()); // Normalize the list for case-insensitive comparison

const isDispatchTool = (toolName) => {
    if (typeof toolName !== 'string') return false;
    const cleanToolName = toolName.trim().toLowerCase();
    if (cleanToolName.endsWith('_mcp_server')) return true;
    return DISPATCH_TOOLS.includes(cleanToolName);
};


/* ========================
 * finalizeContentBlocks - Finalizes all blocks after a stream ends
 * ========================*/
export const finalizeContentBlocks = (blocks) => {
  return blocks.map(block => {
    // Trim final whitespace from text and thinking blocks
    if ((block.type === 'text' || block.type === 'thinking' || block.type === 'code') && typeof block.content === 'string') {
      return { ...block, content: block.content.trim(), isFinal: true };
    }
    // Finalize any tool calls that didn't get an explicit result
    if (block.type === 'tool_code' && !block.isFinal) {
        const isDispatch = isDispatchTool(block.toolName);
        const isPendingOrProcessing = ['pending', 'processing'].includes(block.status);

        // If it's a dispatch tool that's still processing, it means the handoff was successful.
        if (isDispatch && isPendingOrProcessing) {
            return { ...block, isFinal: true, status: 'success' };
        }
        
        // For all other tools, pending/processing at stream end is a true interruption.
        const isInterrupted = isPendingOrProcessing;
        return { ...block, isFinal: true, status: isInterrupted ? 'interrupted' : block.status };
    }
    return { ...block, isFinal: true };
  });
};